import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { group } from '../../@types/AppConfiguration';
import './Canvas.scss';
import { addMapEvents, mapZoomClick } from './MapEvents';
import TooltipComponent from '../TooltipComponent/TooltipComponent';
import config from './config';
import PublicImage from 'src/common/components/PublicImage';

export type MapCanvasProps = {
  selectedLegendTypes: string[];
  groups: group[];
  selectedEntry: group | object;
  updateCard: (groupIndex: number) => void;
  updateZoom: (value: boolean) => void;
  isZoomed: boolean;
};

const MapCanvas = forwardRef(function MapCanvas(
  props: MapCanvasProps,
  viewport,
) {
  const canvas = useRef(null);
  const selectedLegendTypes = props.selectedLegendTypes;
  const groups = props.groups;
  const updateCard = props.updateCard;
  const updateZoom = props.updateZoom;
  const isZoomed = props.isZoomed;
  const selectedEntry = props.selectedEntry;
  const [isHovering, setIsHovering] = useState(false);
  const [circleIndex, setIndex] = useState(0);
  const worldMapIMGAsset = 'images/map.webp'; // static path to public folder asset

  useEffect(() => {
    addMapEvents(canvas, viewport);
  }, []);

  const mapClickHandler = (
    viewportRef: any,
    x: number,
    y: number,
    index: number,
    zoom: boolean,
  ) => {
    if (!zoom) {
      mapZoomClick(viewportRef, x, y);
      updateZoom(true);
    }
    updateCard(index);
  };
  const hoverHandler = (bool = false, index = 0) => {
    setIsHovering(bool);
    setIndex(index);
  };
  const tooltipRender = (title = '', index = 0) => {
    const x = isZoomed ? 20 : 7;
    const y = isZoomed ? 10 : -25;
    const style = isZoomed
      ? {}
      : {
          transform: 'scale(3, 3)',
        };

    return (
      <foreignObject
        key={index}
        x={x}
        y={y}
        width="auto"
        height="auto"
        style={style}
        id="tooltipComponent"
      >
        <TooltipComponent text={title} />
      </foreignObject>
    );
  };

  return (
    <svg
      id="canvas"
      viewBox={`0 0 ${config.canvas.width} ${config.canvas.height}`}
      preserveAspectRatio="xMidYMid meet"
      ref={canvas}
    >
      {/* @ts-expect-error ref.current will never be null in this context */}
      <g id="viewport" ref={viewport}>
        {[-1, 0, 1].map((mapIndex) => {
          const selectedEntryStringfied = JSON.stringify(selectedEntry);
          const mapBasePositionX = mapIndex * config.canvas.width;

          return (
            <React.Fragment key={mapIndex}>
              <PublicImage
                key={`map-image-${mapIndex}`}
                className="map-image"
                imgPath={worldMapIMGAsset}
                widgetId="world-map-leadspace"
                x="0"
                y="0"
                width={config.canvas.width}
                height={config.canvas.height}
                alt="An image of a world map"
                style={{
                  transform: `translate(${mapBasePositionX}px, 0px)`,
                }}
              />
              {groups.map((group, groupIndex) => {
                const x = mapBasePositionX + group.location.x;
                const y = group.location.y;
                const items = [];

                const hasValidTypes =
                  group.types.filter((type) =>
                    selectedLegendTypes.includes(type),
                  ).length > 0;

                const filterTypes = () => {
                  const filteredArray: string[] = [];
                  group.types.forEach((type: string) => {
                    if (!filteredArray.includes(type)) {
                      filteredArray.push(type);
                    }
                  });
                  return filteredArray;
                };

                const filteredTypes = filterTypes();
                const colorClassDef = () => {
                  if (
                    filteredTypes.includes('federal-data-center') &&
                    selectedLegendTypes.includes('federal-data-center')
                  ) {
                    return 'magenta';
                  } else if (
                    filteredTypes.includes('data-centers') &&
                    selectedLegendTypes.includes('data-centers')
                  ) {
                    return 'blue';
                  } else if (
                    filteredTypes.includes('multi-zone-region') &&
                    selectedLegendTypes.includes('multi-zone-region') &&
                    !selectedLegendTypes.includes('network-pop')
                  ) {
                    return 'teal';
                  } else if (
                    filteredTypes.includes('network-pop') &&
                    selectedLegendTypes.includes('network-pop')
                  ) {
                    return 'purple';
                  } else {
                    return 'no-color-defined';
                  }
                };
                const colorDef = colorClassDef();
                const borderDef = () => {
                  if (
                    filteredTypes.includes('multi-zone-region') &&
                    selectedLegendTypes.includes('multi-zone-region')
                  ) {
                    return colorDef + '-with-border';
                  } else {
                    return colorDef;
                  }
                };

                const colorClass = borderDef();

                if (hasValidTypes) {
                  const tooltipOpened =
                    selectedEntryStringfied === JSON.stringify(group);

                  items.push(
                    <g
                      key={`group-${groupIndex}-${mapIndex}`}
                      className={`group ${tooltipOpened ? 'active' : ''}`}
                      style={{
                        transform: `translate(${x}px, ${y}px)`,
                      }}
                      onClick={() => {
                        mapClickHandler(viewport, x, y, groupIndex, isZoomed);
                      }}
                      onMouseOver={() => hoverHandler(true, groupIndex)}
                      onMouseOut={() => hoverHandler(false, -1)}
                    >
                      <circle
                        cx="20"
                        cy="20"
                        r="20"
                        x="0"
                        y="0"
                        className={colorClass}
                      />
                    </g>,
                  );
                }
                return items;
              })}
              {groups.map((group, groupIndex) => {
                const x = mapBasePositionX + group.location.x;
                const y = group.location.y;

                const items = [];

                const hasValidTypes =
                  group.types.filter((type) =>
                    selectedLegendTypes.includes(type),
                  ).length > 0;

                if (hasValidTypes) {
                  const tooltipOpened =
                    selectedEntryStringfied === JSON.stringify(group);

                  items.push(
                    <g
                      key={`group-${groupIndex}-${mapIndex}`}
                      className={`group ${
                        tooltipOpened ||
                        (isHovering && circleIndex === groupIndex)
                          ? 'active'
                          : ''
                      }`}
                      style={{
                        transform: `translate(${x}px, ${y}px)`,
                      }}
                    >
                      {tooltipRender(group.card.title, groupIndex)}
                    </g>,
                  );
                }
                return items;
              })}
            </React.Fragment>
          );
        })}
      </g>
    </svg>
  );
});

export default MapCanvas;
